import { useEffect } from "react";

const BASE       = "Medical Peptide Store";
const PROD_HOST  = "https://medicalpeptidestore.com";
const DEFAULT_TITLE = `${BASE} — Research Reference Standards`;
const DEFAULT_DESCRIPTION =
  "A U.S.-based catalog of laboratory peptide reference standards from Medical Peptide Store. Signature-required delivery, lot documentation. Research use only.";
const DEFAULT_OG_IMAGE = `${PROD_HOST}/og-default.png`;
const MAX_DESCRIPTION = 160;

/** Safety net so no page ships a meta description longer than 160 characters. */
export function clampDescription(text) {
  const t = String(text).replace(/\s+/g, " ").trim();
  if (t.length <= MAX_DESCRIPTION) return t;
  const cut = t.slice(0, MAX_DESCRIPTION - 1);
  return `${cut.slice(0, cut.lastIndexOf(" ")).replace(/[\s,;:.—-]+$/, "")}…`;
}

/**
 * Sets all indexable metadata for the current route.
 *
 *   usePageMeta({
 *     title:       "GLP-3R | Research Reference Standard",   // "· Medical Peptide Store" suffix auto-added
 *     description: "…",
 *     canonicalPath: "/product/glp-3r-reference-standard",  // relative — canonical uses PROD_HOST
 *     ogImage:     "…absolute URL…",                         // optional per-page override
 *     robots:      "noindex, follow",                        // optional (default: index, follow …)
 *     jsonLd:      [{...}, {...}]                            // 0..N JSON-LD blocks to inject
 *   });
 *
 * All values are cleaned up on unmount so SPA route transitions never leak
 * stale meta.
 */
export function usePageMeta({
  title,
  description,
  canonicalPath,
  ogImage,
  ogType = "website",
  robots,
  jsonLd = [],
} = {}) {
  // Extract stringified jsonLd as a stable key so the useEffect deps array
  // can be statically analysed and inline object literals don't re-run.
  const jsonLdKey = JSON.stringify(jsonLd);
  useEffect(() => {
    const restore = [];

    // ---- <title> ---- (brand suffix dropped when it would exceed 70 chars)
    const suffixed = title ? `${title} | ${BASE}` : DEFAULT_TITLE;
    const finalTitle = suffixed.length > 70 && title ? title : suffixed;
    const prevTitle = document.title;
    document.title = finalTitle;
    restore.push(() => { document.title = prevTitle; });

    // ---- meta helpers ----
    const setMeta = (attr, key, val, kind) => {
      if (val === undefined || val === null) return;
      const sel = `meta[${attr}="${key}"]`;
      let el = document.head.querySelector(sel);
      const created = !el;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        if (kind === "og") el.setAttribute("property", key);
        document.head.appendChild(el);
      }
      const prev = el.getAttribute("content");
      el.setAttribute("content", val);
      restore.push(() => {
        if (created) el.remove();
        else if (prev !== null) el.setAttribute("content", prev);
        else el.removeAttribute("content");
      });
    };

    const finalDesc = clampDescription(description || DEFAULT_DESCRIPTION);
    const finalOgImage = ogImage || DEFAULT_OG_IMAGE;
    const canonicalHref = canonicalPath ? `${PROD_HOST}${canonicalPath}` : `${PROD_HOST}${window.location.pathname}`;

    setMeta("name", "description", finalDesc);
    setMeta("name", "robots", robots || "index, follow, max-snippet:-1, max-image-preview:large");

    setMeta("property", "og:type", ogType);
    setMeta("property", "og:title", finalTitle);
    setMeta("property", "og:description", finalDesc);
    setMeta("property", "og:url", canonicalHref);
    setMeta("property", "og:image", finalOgImage);
    setMeta("property", "og:site_name", BASE);

    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", finalTitle);
    setMeta("name", "twitter:description", finalDesc);
    setMeta("name", "twitter:image", finalOgImage);

    // ---- canonical <link> ----
    let canonicalEl = document.head.querySelector('link[rel="canonical"]');
    const canonicalCreated = !canonicalEl;
    if (!canonicalEl) {
      canonicalEl = document.createElement("link");
      canonicalEl.setAttribute("rel", "canonical");
      document.head.appendChild(canonicalEl);
    }
    const canonicalPrev = canonicalEl.getAttribute("href");
    canonicalEl.setAttribute("href", canonicalHref);
    restore.push(() => {
      if (canonicalCreated) canonicalEl.remove();
      else if (canonicalPrev !== null) canonicalEl.setAttribute("href", canonicalPrev);
    });

    // ---- JSON-LD ----
    const jsonLdList = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
    const scripts = jsonLdList.filter(Boolean).map((block) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute("data-page-jsonld", "1");
      s.textContent = JSON.stringify(block);
      document.head.appendChild(s);
      return s;
    });
    restore.push(() => scripts.forEach((s) => s.remove()));

    return () => { while (restore.length) restore.pop()(); };
    // jsonLd is intentionally referenced as `jsonLdKey` (a stable JSON string)
    // so inline object literals don't cause endless re-runs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    title, description, canonicalPath, ogImage, ogType, robots,
    jsonLdKey,
  ]);
}

/** Convenience: mark a route as noindex (cart, checkout, account, admin, etc.).
 *  Accepts optional title/description so a private-but-real page like
 *  `/cart` or `/checkout/shipping` still ships a unique, complete title +
 *  meta description rather than the default homepage boilerplate. */
export function useNoIndex(canonicalPath, opts = {}) {
  const { title, description } = opts;
  usePageMeta({
    title,
    description,
    canonicalPath,
    robots: "noindex, follow",
  });
}

/**
 * Soft-404 helper for the SPA. Emits:
 *   <meta name="prerender-status-code" content="404">
 *   <meta name="robots" content="noindex, nofollow">
 * and titles the tab "404 — Page not found | Medical Peptide Store".
 *
 * True HTTP 404 status still requires ingress-level configuration for
 * SPA fallback URLs — see /app/memory/ROUTING_404_NOTES.md.
 */
export function useSoft404(canonicalPath) {
  useEffect(() => {
    const restore = [];

    // Title
    const finalTitle = `404 — Page not found | ${BASE}`;
    const prevTitle = document.title;
    document.title = finalTitle;
    restore.push(() => { document.title = prevTitle; });

    // Meta tags
    const upsert = (name, content) => {
      const sel = `meta[name="${name}"]`;
      let el = document.head.querySelector(sel);
      const created = !el;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      const prev = el.getAttribute("content");
      el.setAttribute("content", content);
      restore.push(() => {
        if (created) el.remove();
        else if (prev !== null) el.setAttribute("content", prev);
      });
    };
    upsert("prerender-status-code", "404");
    upsert("robots", "noindex, nofollow");

    return () => { while (restore.length) restore.pop()(); };
  }, [canonicalPath]);
}

export const SEO_CONSTANTS = { PROD_HOST, BASE, DEFAULT_TITLE, DEFAULT_DESCRIPTION, DEFAULT_OG_IMAGE };
